import type { AxiosInstance } from 'axios';
import { makeAuthHeaders } from './utils/make-auth-headers';

// Mirrors the Rust `SecurityLogEntry` / `SecurityLogResponse` in
// the backend's `audits::api`. Keep these in sync when the server shape
// changes.
export interface SecurityLogEntry {
  id: string;
  timestamp: string; // RFC3339
  operation_label: string;
  /** Sign-in method ("SSO", "Nostr", …); null for events not tied to one. */
  method: string | null;
  location: string;
  device: string;
  success: boolean;
}

export interface SecurityLogResponse {
  data: SecurityLogEntry[];
  total: number;
}

export interface SecurityLogQuery {
  page?: number;
  per_page?: number;
  /** `false`: failures only. */
  success?: boolean;
  /** `sign_in` for sign-in events only. */
  category?: string;
}

export interface RustySecurityLogProps {
  client: AxiosInstance;
}

export interface RustySecurityLogSpec {
  getSecurityLog(query: SecurityLogQuery, accessToken: string): Promise<SecurityLogResponse>;
}

export class RustySecurityLog implements RustySecurityLogSpec {
  private client: AxiosInstance;

  constructor({ client }: RustySecurityLogProps) {
    this.client = client;
  }

  async getSecurityLog(query: SecurityLogQuery, accessToken: string): Promise<SecurityLogResponse> {
    const params: Record<string, string | number | boolean> = {};
    if (query.page !== undefined) params.page = query.page;
    if (query.per_page !== undefined) params.per_page = query.per_page;
    if (query.success !== undefined) params.success = query.success;
    if (query.category !== undefined) params.category = query.category;
    const result = await this.client.get<SecurityLogResponse>('/a/audit', {
      params,
      headers: makeAuthHeaders(accessToken),
    });
    return result.data;
  }
}
